import { trackGa4Checkout } from '../../lib/ga4';
import React, {useCallback, useState, useEffect, useMemo, useRef} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useUserContext } from "../../contexts";
import { meFn, subscribeUserFn, checkoutBundleFn, Analytics, getProducts, getCampaignBundles, getBundlesFn } from '../../lib/api';
import { Signup } from './';
import { Spinner } from '../Spinner';
import { getEnvironment, setCookie } from '../../lib/utils';
import { getNativeAppClient } from '../NativeAppHandoff';
import {
  clearBillingPeriodExperiment,
  getBillingPeriodExperimentContext,
} from '../../lib/billingPeriodExperiment';

import './OnlySignup.scss';

const TERMS_URL = 'https://www.productioncrate.com/terms.slim.html';
const PRIVACY_URL = 'https://www.productioncrate.com/privacy.html';
const FREE_PLAN_IDS = {
  development: 67,
  qa: 67,
  production: 1,
};
const MARKETPLACE_CART_CHECKOUT_PATH = '/marketplace/cart?cart_intent=checkout';

const hasTermsPrivacyConsent = (user) => {
  const settings = user?.settings && typeof user.settings === 'object' ? user.settings : {};
  const optInValue = settings.terms_privacy_optin;
  return optInValue === true
    || optInValue === 'true'
    || optInValue === 1
    || optInValue === '1'
    || !!settings.terms_privacy_optin_at;
};

const hasPaidSubscription = (user) => {
  const plan = user?.team?.subscription?.plan;
  if (!plan) {
    return false;
  }

  const price = Number(plan.price ?? 0);
  const normalizedName = String(plan.name ?? '').trim().toLowerCase();
  return Number.isFinite(price) && price > 0 && normalizedName !== 'free' && !normalizedName.includes('false pro');
};

const parseAddonPriceIdsQueryValue = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return [];
  }

  return value
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((id, index, source) => Number.isFinite(id) && id > 0 && source.indexOf(id) === index);
};

const parseBundleLicenseTierId = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const formatBundleName = (bundle) => {
  const name = bundle?.name ? String(bundle.name).trim().toLowerCase().replace(/\s+/g, '_') : null;
  return name ? `${name}_lifetime` : 'bundle_lifetime';
};

const findPlanById = (products, planId) => {
  let resolvedPlan = null;

  (products ?? []).some((product) => {
    const plans = Array.isArray(product?.plans) ? product.plans : [];
    resolvedPlan = plans.find((candidatePlan) => +candidatePlan?.id === +planId) ?? null;
    return !!resolvedPlan;
  });

  return resolvedPlan;
};

const resumeMarketplaceCartCheckout = () => {
  window.location.assign(MARKETPLACE_CART_CHECKOUT_PATH);
};

const OnlySignup = () => {

  //Custom Hooks
  const {user, isAuthenticating, unloadUser, reloadCurrentUser} = useUserContext();

  const [searchParams] = useSearchParams();
  let { planId, provider} = useParams();
  const checkoutItemType = searchParams.get('checkout_item_type');
  const normalizedCheckoutItemType = String(checkoutItemType ?? '').trim().toLowerCase();
  const isBundleRequested = normalizedCheckoutItemType === 'bundle';
  const isMarketplaceCartRequested = normalizedCheckoutItemType === 'marketplace_cart';
  const bundleLicenseTierId = useMemo(
    () => parseBundleLicenseTierId(searchParams.get('bundle_license_tier_id')),
    [searchParams]
  );
  const redirectUrl = useMemo(() => {
    const rawValue = searchParams.get('redirect_url');
    if (typeof rawValue !== 'string') {
      return null;
    }
    const trimmedValue = rawValue.trim();
    return trimmedValue || null;
  }, [searchParams]);
  const addonPriceIds = useMemo(() => (
    parseAddonPriceIdsQueryValue(searchParams.get('addon_price_ids') || searchParams.get('addon_ids'))
  ), [searchParams]);
  const runtimeEnvironment = useMemo(() => getEnvironment(), []);
  const knownFreePlanId = FREE_PLAN_IDS[runtimeEnvironment] ?? FREE_PLAN_IDS.production;
  const isKnownFreeSignup = !isBundleRequested && !isMarketplaceCartRequested && +planId === +knownFreePlanId;

  //State to control the loading spinner
  const [showLoading, setShowLoading] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [ctaAfterSubscribe, setCtaAfterSubscribe] = useState('');
  const [signupTrigger, setSignupTrigger] = useState(0);
  const [showConsentStep, setShowConsentStep] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [termsPrivacyOptIn, setTermsPrivacyOptIn] = useState(false);
  const [termsPrivacyOptInError, setTermsPrivacyOptInError] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [consentCtaLabel, setConsentCtaLabel] = useState(isKnownFreeSignup ? 'Continue' : 'Continue to checkout');
  const [checkoutConsentCompleted, setCheckoutConsentCompleted] = useState(false);
  const signupFlowRef = useRef(false);
  const pendingSignupRef = useRef(false);
  const pendingCheckoutTrackSignupRef = useRef(false);
  const checkoutAttemptKeyRef = useRef(null);
  const checkoutConsentCompletedRef = useRef(false);
  const billingPeriodExperimentExclusionRef = useRef(null);
  const termsPrivacyCheckboxRef = useRef(null);
  const shouldResumeAfterLogin = searchParams.get('resume') === '1';
  const nativeApp = getNativeAppClient(searchParams);

  useEffect(() => {
    // Ensure the dedicated signup route always starts at viewport origin.
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (!user || !hasPaidSubscription(user)) {
      return;
    }

    const experimentContext = getBillingPeriodExperimentContext();
    if (!experimentContext || billingPeriodExperimentExclusionRef.current === experimentContext.assignment_id) {
      return;
    }

    billingPeriodExperimentExclusionRef.current = experimentContext.assignment_id;
    Analytics.sendCustomEvent('billing_period_experiment_excluded', {
      reason: 'paid_subscription',
      assignment_id: experimentContext.assignment_id,
    });
    clearBillingPeriodExperiment();
  }, [user]);

  const formatPlanName = (plan) => {
    if(!plan){
      return null;
    }
    const name = plan.name ? String(plan.name).trim().toLowerCase().replace(/\s+/g, '_') : null;
    let period = null;
    if(plan.periodicity_type){
      const normalized = String(plan.periodicity_type).toLowerCase();
      if(normalized.startsWith('month')){
        period = 'monthly';
      }else if(normalized.startsWith('year') || normalized.startsWith('annual')){
        period = 'yearly';
      }else{
        period = normalized;
      }
    }
    return [name, period].filter(Boolean).join('_') || null;
  };

  const signupSuccessCallback = () => {
    pendingSignupRef.current = true;
    setSignupTrigger(value => value + 1);
    setShowLoading(true);
    if(planId){
        console.log(isMarketplaceCartRequested ? "Marketplace cart checkout will happen" : "Plan selected, subscription will happen");
    }else{
        console.log("No plan selected, nothing to do");
    }
  }

  const handleConsentContinue = async (event) => {
    event.preventDefault();
    if(consentSaving){
      return;
    }
    setConsentError(false);
    if(!termsPrivacyOptIn){
      setTermsPrivacyOptInError(true);
      setConsentError('Please agree to the Terms and Privacy Policy to continue.');
      return;
    }
    setTermsPrivacyOptInError(false);
    setConsentSaving(true);
    try{
      const response = await meFn({
        newsletter: !!newsletterOptIn,
        terms_privacy_optin: true,
        terms_privacy_optin_at: new Date().toISOString(),
      });
      if(!response){
        throw new Error('Consent settings update failed.');
      }
      setShowConsentStep(false);
      checkoutConsentCompletedRef.current = true;
      setCheckoutConsentCompleted(true);
      setShowLoading(true);
      if(isMarketplaceCartRequested){
        resumeMarketplaceCartCheckout();
        return;
      }
      if(typeof reloadCurrentUser === 'function'){
        Promise.resolve(reloadCurrentUser()).catch(e => {
          console.log("Problem while reloading user after consent", e);
        });
      }
    }catch(e){
      console.log("Problem while saving signup consent", e);
      setConsentError('There was a problem saving your preferences. Please try again.');
    }finally{
      setConsentSaving(false);
    }
  };
  

  const subscribeUser = useCallback(async (trackSignup = false) => {
    if (isMarketplaceCartRequested) {
      resumeMarketplaceCartCheckout();
      return;
    }

    let products = [];
    let bundles = [];
    let plan = null;
    let bundle = null;
    let campaign = searchParams.get("c")??false;
    let paymentLinkId = searchParams.get("payment_link_id") ?? false;
    try{
      const responseProducts = await getProducts({campaign, paymentLinkId});
      products = Array.isArray(responseProducts) ? responseProducts : [];
    }catch(e){
      console.log("Problem while getting products", e);
    }

    try{
      bundles = isBundleRequested
        ? await getBundlesFn()
        : await getCampaignBundles({campaign});
    }catch(e){
      console.log("Problem while getting bundles", e);
    }

    products.forEach(prod => {
        if(plan!==null){
            return;
        }
        prod.plans.forEach(p => {
            if(+p.id === +planId){
                plan = p;
                return;
            }
        });
    });

    if (Array.isArray(bundles)) {
      bundle = bundles.find((tempBundle) => +tempBundle?.id === +planId) ?? null;
    }

    const checkoutItem = isBundleRequested
      ? bundle
      : (plan ?? bundle);
    const selectedBundleTier = Array.isArray(bundle?.license_tiers)
      ? bundle.license_tiers.find((tier) => Number(tier?.id) === bundleLicenseTierId) ?? null
      : null;
    const checkoutItemPrice = Number(selectedBundleTier?.price ?? checkoutItem?.price ?? 0);
    const checkoutItemPriceSafe = Number.isFinite(checkoutItemPrice) ? checkoutItemPrice : 0;
    const isBundleCheckout = isBundleRequested
      ? !!bundle
      : (!!bundle && !plan);
    const checkoutItemName = isBundleCheckout
      ? formatBundleName(bundle)
      : formatPlanName(plan);

    if (!user) {
      console.log("No user found, cannot continue checkout", { planId, campaign, paymentLinkId });
      setShowLoading(false);
      setVerificationError("There was a problem while continuing checkout.");
      setCtaAfterSubscribe('error');
      return;
    }

    if (!checkoutItem) {
      console.log("No checkout item found by id, proceeding with backend resolution", { planId, campaign, paymentLinkId });
      if (isBundleRequested) {
        setShowLoading(false);
        setVerificationError("Bundle could not be resolved. Please go back and try again.");
        setCtaAfterSubscribe('error');
        return;
      }
    }

    if (trackSignup && checkoutItem) {
      try{
        Analytics.signup(checkoutItemPriceSafe, {
            name: `${user.first_name || ''} ${user.last_name || ''} (${user.username || ''})`,
            email: user.email,
            team_id: user?.team?.id,
            plan_id: checkoutItem?.id ?? planId,
            plan_name: checkoutItemName
        });
      }catch(e){
          console.log("Problem with analytics signup event", e);
      }
    }

    
    const resolvedProvider = provider==='s'?'stripe':'paypal';
    const postData = {
      provider: resolvedProvider,
      planId,
      addonPriceIds,
      nativeApp,
    };

    if(!isBundleCheckout && plan && Number(plan.price) === 0){
        //free registration, nothing to do but get redirected to member area
        const successUrl = nativeApp ? `/member/success?native_app=${encodeURIComponent(nativeApp)}` : '/account';
        setTimeout(() => document.location.href = successUrl, 1000);
        return;
    }
    const checkoutPromise = isBundleCheckout
      ? checkoutBundleFn({ provider: resolvedProvider, bundleId: planId, bundleLicenseTierId, redirectUrl, nativeApp })
      : subscribeUserFn(postData);

    checkoutPromise.then(data => {
      const checkoutUrl = data?.checkout_url || data?.url;
      if(checkoutUrl){
          trackGa4Checkout(data?.analytics);
          console.log("REDIRECT TO URL", data);
          if (user.settings?.subscribeTo?.planId){
            meFn({subscribeTo: null}).then( r => document.location.href = checkoutUrl );
          } else {
            document.location.href = checkoutUrl;
          }
      } else if(data.type ==='success') {
        console.log("SUCCESS", data);
        if (nativeApp) {
          document.location.href = `/member/success?native_app=${encodeURIComponent(nativeApp)}`;
          return;
        }
        setShowLoading(false);
      } else if(data.type === 'verify_email'){
        meFn({
          subscribeTo: {
            planId: planId,
            provider: provider,
            campaign: campaign,
            payment_link_id: paymentLinkId,
            addon_price_ids: addonPriceIds,
            checkout_item_type: isBundleCheckout ? 'bundle' : null,
            bundle_license_tier_id: isBundleCheckout ? bundleLicenseTierId : null,
            redirect_url: isBundleCheckout ? redirectUrl : null,
            native_app: nativeApp,
          }
        }).then(d => {
          setShowLoading(false);
          setVerificationError(data.msg);
          setCtaAfterSubscribe(data.type);
        });
      } else if(data.msg){
          console.log("ERROR OR WARNING", data);
          setShowLoading(false);
          setVerificationError(data.msg);
          setCtaAfterSubscribe(data.type);
      } else {
          setShowLoading(false);
      }
    })
    .catch(e => {
        console.log("Problem while processing checkout", e);
        setVerificationError(
          isBundleCheckout
            ? "There was a problem while checking out your bundle."
            : "There was a problem while subscribing you to the plan."
        );
        setShowLoading(false);
        setCtaAfterSubscribe('error');
    });

  }, [
    addonPriceIds,
    bundleLicenseTierId,
    isBundleRequested,
    isMarketplaceCartRequested,
    planId,
    provider,
    redirectUrl,
    nativeApp,
    searchParams,
    user,
  ]);

  const closeCallback = () => {
    //a react function for history back
    window.history.back();
  };

  useEffect(() => {  
    if(!isAuthenticating && !user){
        console.log("HIDE LOADING");
        console.log("is not authenticating: show loading off");
        setShowLoading(false);
    }else if(isAuthenticating){
        console.log("SHOW LOADING");
        setShowLoading(true);
    }
  }, [isAuthenticating, user]);

  useEffect(() => {
    if(!user && !isAuthenticating){
      signupFlowRef.current = true;
    }
  }, [isAuthenticating, user]);

  useEffect(() => {
    if(showConsentStep){
      setTimeout(() => termsPrivacyCheckboxRef.current?.focus(), 0);
    }
  }, [showConsentStep]);

  useEffect(() => {
    setConsentCtaLabel(isKnownFreeSignup ? 'Continue' : 'Continue to checkout');
  }, [isKnownFreeSignup]);

  useEffect(() => {
    if (!showConsentStep || !planId || isBundleRequested || isMarketplaceCartRequested) {
      return;
    }

    let isCancelled = false;
    const campaign = searchParams.get('c') ?? false;
    const paymentLinkId = searchParams.get('payment_link_id') ?? false;

    const resolveConsentCtaLabel = async () => {
      try {
        const responseProducts = await getProducts({ campaign, paymentLinkId });
        const resolvedPlan = findPlanById(
          Array.isArray(responseProducts) ? responseProducts : [],
          planId
        );
        if (isCancelled || !resolvedPlan) {
          return;
        }
        setConsentCtaLabel(Number(resolvedPlan.price) === 0 ? 'Continue' : 'Continue to checkout');
      } catch (e) {
        console.log('Problem while resolving consent CTA label', e);
      }
    };

    resolveConsentCtaLabel();

    return () => {
      isCancelled = true;
    };
  }, [isBundleRequested, isMarketplaceCartRequested, planId, searchParams, showConsentStep]);

  useEffect(() => {
    if(user && planId){
      if(signupFlowRef.current && !pendingSignupRef.current && !shouldResumeAfterLogin){
        return;
      }
      const shouldTrackSignup = pendingSignupRef.current || pendingCheckoutTrackSignupRef.current;
      if(!checkoutConsentCompletedRef.current && !checkoutConsentCompleted && !hasTermsPrivacyConsent(user)){
        pendingCheckoutTrackSignupRef.current = shouldTrackSignup;
        pendingSignupRef.current = false;
        signupFlowRef.current = false;
        setShowLoading(false);
        setShowConsentStep(true);
        return;
      }
      const checkoutAttemptKey = [user.id, planId, provider, addonPriceIds.join(','), bundleLicenseTierId, nativeApp, signupTrigger].join(':');
      if (checkoutAttemptKeyRef.current === checkoutAttemptKey) {
        return;
      }
      checkoutAttemptKeyRef.current = checkoutAttemptKey;
      console.log("susbcribe user: show loading on");
      setShowLoading(true);
      pendingSignupRef.current = false;
      pendingCheckoutTrackSignupRef.current = false;
      signupFlowRef.current = false;
      setShowConsentStep(false);
      subscribeUser(shouldTrackSignup);
    }
  }, [addonPriceIds, bundleLicenseTierId, checkoutConsentCompleted, nativeApp, planId, provider, shouldResumeAfterLogin, signupTrigger, subscribeUser, user]);

  useEffect(() => {
    console.log("PLan to subscribe to", planId);
  }, [planId]);

  const handleTryDifferentEmail = () => {
    unloadUser();
    setTimeout(() => window.location.reload(), 100);
  }

  const handleLoginInstead = () => {
    const returnPath = isMarketplaceCartRequested
      ? MARKETPLACE_CART_CHECKOUT_PATH
      : (() => {
          const params = new URLSearchParams(searchParams.toString());
          params.set('resume', '1');
          const query = params.toString();
          return `/signup/${planId}/${provider}${query ? `?${query}` : ''}`;
        })();
    setCookie('crateLoginRedirect', returnPath, 1);
    document.location.href = '/login';
  };

  return (
    <>
      {(showLoading || isAuthenticating) && 
      <div style={{display: 'flex', alignItems:'center', justifyContent: 'center', width: '100%', height: '100vh'}}>
          <Spinner style={{position: 'relative'}} />
      </div>
      }
      {!showLoading && verificationError && 
      <div className="form" style={{display: 'flex', alignItems:'center', justifyContent: 'center', width: '100%', height: '100vh', flexDirection: 'column', gap: '20px'}}>
          <div className="title m-b-16" style={{color: '#FFF', fontSize: '18px'}}>{verificationError}</div>
          {ctaAfterSubscribe === 'error' &&
          <div style={{display: 'flex', gap: '16px', alignItems: 'center'}}>
            <div onClick={handleTryDifferentEmail} className="cta modern white">Try a different Email</div>
            <a href="/register" className="cta modern accent">View other Plans</a>
          </div>
          }
          {ctaAfterSubscribe === 'verify_email' &&
          <div style={{display: 'flex', gap: '16px', alignItems: 'center'}}>
            <div style={{border: 'none', fontSize: '16px', fontWeight: '500', color: '#FFF'}} >Already verified?: </div>
            <div onClick={() => window.location.reload()} className="cta modern accent">Reload and Continue</div>
          </div>
          } 
      </div>
      }
      {!showLoading && showConsentStep && user &&
      <div className="signup visible checkout-consent-step">
        <div className="signup-grid-overlay" aria-hidden="true" />
        <form className="form" onSubmit={handleConsentContinue}>
          <div className="signup-card checkout-consent-card">
            <div className="title">Review terms</div>
            <div className="terms-frame">
              <iframe title="ProductionCrate Terms and Conditions" src={TERMS_URL} />
            </div>

            <div className="terms-links">
              <a target="_blank" rel="noreferrer" href={TERMS_URL}>Open Terms</a>
              <span>&middot;</span>
              <a target="_blank" rel="noreferrer" href={PRIVACY_URL}>Privacy Policy</a>
            </div>

            <div className="consent-options">
              <label className="newsletter" htmlFor="checkout-newsletter-checkbox">
                <input
                  type="checkbox"
                  checked={!!newsletterOptIn}
                  onChange={() => setNewsletterOptIn(!newsletterOptIn)}
                  id="checkout-newsletter-checkbox"
                />
                <span>Send me new plugins, features and site updates (a few times a month)</span>
              </label>

              <label className={`consent ${termsPrivacyOptInError ? 'consent-error' : ''}`} htmlFor="checkout-terms-privacy-checkbox">
                <input
                  ref={termsPrivacyCheckboxRef}
                  type="checkbox"
                  className={termsPrivacyOptInError ? 'checkbox-error' : ''}
                  checked={termsPrivacyOptIn}
                  onChange={() => {
                    setTermsPrivacyOptIn(!termsPrivacyOptIn);
                    setTermsPrivacyOptInError(false);
                    setConsentError(false);
                  }}
                  id="checkout-terms-privacy-checkbox"
                />
                <span>
                  I agree to the{' '}
                  <a className="consent-link" target="_blank" rel="noreferrer" href={TERMS_URL} onClick={(event) => event.stopPropagation()}>
                    Terms
                  </a>
                  {' '}and{' '}
                  <a className="consent-link" target="_blank" rel="noreferrer" href={PRIVACY_URL} onClick={(event) => event.stopPropagation()}>
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
            </div>

            {consentError && <div className="error-msg">{consentError}</div>}

            <button
              type="submit"
              className={`cta-btn${termsPrivacyOptIn ? ' ready' : ''}${consentSaving ? ' loading' : ''}`}
              disabled={consentSaving}
            >
              {consentSaving ? 'Saving...' : consentCtaLabel}
            </button>
          </div>
        </form>
      </div>
      }
      {!showLoading && !user && 
      <Signup
        signupSuccessCallback={signupSuccessCallback}
        planId={planId}
        provider={provider}
        closeCallback={closeCallback}
        notClosable={true}
        loginInsteadCallback={handleLoginInstead}
      />
      }
    </>
  );
};

export default OnlySignup;
